import isString from "lodash-es/isString";
import {
  AlertTriangle,
  AlertTriangle as AlertIcon,
  Info as InfoIcon,
} from "lucide-react";
import React, { CSSProperties, ReactNode, useMemo } from "react";
import { Else, If, Then } from "react-if";
import CheckIcon from "@/icons/check.svg?react";
import "./message.scss";

const WarningIcon = (props: React.ComponentProps<typeof AlertTriangle>) => (
  <AlertTriangle color="var(--tlx-status-error-text)" {...props} />
);

// Lazy-loaded: pulls in react-markdown + sql-formatter/prismjs, which should
// only ship to users who actually render a Message with text.
const MarkDown = React.lazy(() => import("@/components/Markdown"));

interface IPropsMessage {
  text: string | ReactNode;
  type?: "warning" | "info" | "success" | "alert";
  style?: CSSProperties;
}

export const Message: React.FC<IPropsMessage> = ({
  text,
  type = "info",
  style,
}) => {
  const Icon = useMemo(() => {
    switch (type) {
      case "info":
        return InfoIcon;

      case "warning":
        return WarningIcon;

      case "success":
        return CheckIcon;

      case "alert":
        return AlertIcon;

      default:
        return InfoIcon;
    }
  }, [type]);

  if (!text) return null;

  return (
    <div className={`tlx-messages tlx-messages__${type}`} style={style}>
      <div className={`tlx-messages__icon-${type} k-d-flex`}>
        <Icon height={24} width={24} />
      </div>
      <div className="tlx-messages__message">
        <If condition={isString(text)}>
          <Then>
            <React.Suspense fallback={text}>
              <MarkDown>{text as string}</MarkDown>
            </React.Suspense>
          </Then>
          <Else>{text}</Else>
        </If>
      </div>
    </div>
  );
};

export default Message;
